<script setup lang="ts">
import { CoverFeatureItem } from './CoverFeatures.vue';

const emit = defineEmits<{
  (e: 'click', feature: CoverFeatureItem): void;
}>();

const props = defineProps<{
  feature: CoverFeatureItem;
  iconSize?: number;
}>();

// Get default icon for a feature type
const getDefaultIcon = (feature: CoverFeatureItem) => {
  switch (feature.type) {
    case 'externalMaterial':
      return 'mdi-clipboard-text-outline';
    case 'teachingMaterial':
      return 'fas fa-list-check';
    case 'noAudio':
      return 'mdi-volume-off';
    case 'bookTalk':
      return 'fas fa-comments';
    case 'languages':
      return 'mdi-earth';
    case 'downloaded':
      return 'mdi-cellphone-check';
    default:
      return '';
  }
};

// Get text color class for a feature
const getTextColorClass = (feature: CoverFeatureItem) => {
  if (feature.textColor) {
    return '';
  }

  // Default text colors based on feature type
  switch (feature.type) {
    case 'externalMaterial':
      return 'text-on-warning';
    case 'teachingMaterial':
      return 'text-on-info';
    case 'noAudio':
      return 'text-on-error';
    case 'bookTalk':
      return 'text-on-success';
    case 'languages':
      return 'text-on-secondary';
    case 'downloaded':
      return 'text-on-inverse-surface';
    case 'grade':
      return 'text-on-secondary-container';
    default:
      return 'text-on-surface-variant';
  }
};
</script>

<template>
  <v-tooltip v-if="!!props.feature.tooltip" :location="feature.tooltipLocation ?? 'top'" :offset="[16, 0]">
    <template #activator="{ props: tooltip }">
      <button @click="emit('click', feature)" v-bind="{ ...tooltip }" :aria-label="feature.tooltip">
        <v-icon
          v-if="feature.icon || getDefaultIcon(feature)"
          :size="feature.iconSize ?? iconSize"
          :class="getTextColorClass(feature)"
          :aria-label="feature.tooltip"
        >
          {{ feature.icon || getDefaultIcon(feature) }}
        </v-icon>
        <span v-else-if="feature.content" class="book-feature">{{ feature.content }}</span>
      </button>
    </template>
    <span>{{ feature.tooltip }} </span>
  </v-tooltip>

  <template v-else>
    <button @click="emit('click', feature)" :aria-label="feature.tooltip">
      <v-icon
        v-if="feature.icon || getDefaultIcon(feature)"
        :size="feature.iconSize ?? iconSize"
        :class="getTextColorClass(feature)"
      >
        {{ feature.icon || getDefaultIcon(feature) }}
      </v-icon>
      <span v-else-if="feature.content" class="book-feature">{{ feature.content }}</span>
    </button>
  </template>
</template>

<style scoped lang="scss"></style>
